import React, {useState} from "react";
import {FormikProvider, useFormik} from "formik";
import * as Yup from "yup";
import {orderRuleMethodSchemas, orderRuleMethodsRegistry} from "./methods/methodsHelper";
import {IOrderRuleForm} from "../../../../../store/trafficManagement/orderRules/types";
import {Button, Card, CardBody, CardHeader, CardTitle, Input, Label} from "reactstrap";
import Select from "../../../../../components/Common/Select";
import FeatherIcon from "feather-icons-react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import NoSearchResult from "../../../../../components/Common/NoSearchResult";
import {OrderRuleService} from "../../../../../services/order_rule.service";

interface FormCardOrderRuleProps {
    orderRuleId?: number | null;
    onClose: () => void;
    invalidateOrderRules?: (type?: "create" | "edit") => Promise<void>;
}

const FormCardOrderRule: React.FC<FormCardOrderRuleProps> = ({orderRuleId, onClose, invalidateOrderRules}) => {
    const isEditRule = !!orderRuleId;
    const queryClient = useQueryClient();

    const [approvedSave, setApprovedSave] = useState<boolean>(false)

    const {data: orderRule, isFetching: isFetchingRule} = useQuery({
        queryKey: ["keyRule", orderRuleId],
        queryFn: () => OrderRuleService.getRuleById(orderRuleId || -1),
        enabled: isEditRule
    });

    const onSuccess = () => {
        if (!invalidateOrderRules) return;

        const invalidateType = isEditRule ? "edit" : "create";
        invalidateOrderRules(invalidateType).then(() => {
            onClose();
        });
    }

    const onError = () => {
        setApprovedSave(false);
    }

    const {mutate: createRule, isPending: isPendingCreate} = useMutation({
        mutationFn: (data: IOrderRuleForm) => OrderRuleService.createRule(data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyCreateRule"],
    });

    const {mutate: updateRule, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: IOrderRuleForm) => OrderRuleService.updateRule(orderRuleId!, data),
        onSuccess: onSuccess,
        onError: onError,
        mutationKey: ["keyUpdateRule"],
    });

    const handleSubmitForm = (values: IOrderRuleForm) => {
        setApprovedSave(true)

        if (!isEditRule) createRule(values)
        else updateRule(values)
    }

    const validationSchema: Yup.Schema = Yup.object({
        name: Yup.string().required("Name is required"),
        method: Yup.string().required("Method is a required"),

        rule: Yup.lazy((_, {parent}) => {
            const method = parent.method as keyof typeof orderRuleMethodSchemas;
            return orderRuleMethodSchemas[method] ?? Yup.mixed().notRequired();
        })
    })

    const initialMethod = Object.keys(orderRuleMethodsRegistry)[0];
    const initialRule = orderRuleMethodsRegistry[orderRule?.method || initialMethod].defaultRule;
    const initialValues: IOrderRuleForm = {
        name: orderRule?.name || "",
        method: orderRule?.method || initialMethod,
        rule: orderRule?.rule || initialRule
    };

    const formik = useFormik<IOrderRuleForm>({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });
    const {
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        setErrors,
        setTouched,
        errors,
        touched,
        isValid
    } = formik;

    function methodChange(nextMethod: string) {
        setFieldValue("method", nextMethod);
        setFieldValue("rule", orderRuleMethodsRegistry[nextMethod].defaultRule);
        setErrors({...errors, rule: {}});
        setTouched({...touched, rule: {}});
    }

    const ruleMethods = Object.values(orderRuleMethodsRegistry).map((item) => ({
        value: item.id,
        label: item.label,
    }));

    const selectedMethod = ruleMethods.find((item) => item.value === values.method);

    const MethodComponent = values.method && orderRuleMethodsRegistry[values.method]?.Component;

    const handleMethodSelect = (selected: any) => {
        if (!selected) return
        methodChange(selected.value);
    }

    const handleCancelClick = () => {
        onClose();
    }

    const isSelectableMethod = Object.keys(orderRuleMethodsRegistry).length > 1;
    const isPendingSave = (isPendingCreate || isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyRuleList"]})) && approvedSave;

    return (
        <Card className="mb-3 shadow" color="secondary" outline>
            <CardHeader className="p-3 bg-light border-dark shadow">
                <div className="d-flex align-items-center justify-content-between gap-1">
                    <CardTitle tag="h5" className="fs-5 m-0">
                        {isEditRule ? `Editing Rule #${orderRuleId}` : "Creating Rule"}
                    </CardTitle>

                    <Button className="d-flex p-1" color="danger" onClick={handleCancelClick}>
                        <FeatherIcon icon="x" size={16}/>
                    </Button>
                </div>
            </CardHeader>
            <CardBody>
                {isFetchingRule ? <div className="my-3"><NoSearchResult text="Loading..."/></div>
                    : (<FormikProvider value={formik}>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <Label htmlFor="name" className="mb-1">Name:</Label>
                                <Input
                                    id="name"
                                    name="name"
                                    placeholder="Rule name*"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isPendingSave}
                                    value={values.name}/>

                                {touched.name && errors.name && (
                                    <div className="text-danger font-size-12 mt-1">{errors.name}</div>
                                )}
                            </div>

                            <div className="mb-2">
                                <Label className="mb-1">Method:</Label>
                                <Select
                                    name="method"
                                    placeholder="Select method..."
                                    options={ruleMethods}
                                    value={selectedMethod}
                                    isDisabled={isEditRule || !isSelectableMethod || isPendingSave}
                                    onBlur={() => handleBlur("method")}
                                    onChange={handleMethodSelect}/>

                                {touched.method && errors.method && (
                                    <div className="text-danger font-size-12 mt-1">{errors.method}</div>
                                )}
                            </div>

                            {MethodComponent &&
                                <>
                                    <hr className="mb-2"/>
                                    <MethodComponent rulesPath="rule" methodKey={values.method}
                                                     isPendingSave={isPendingSave}/>
                                </>
                            }

                            <Button type="submit" color="success" className="mt-3" disabled={!isValid || isPendingSave}>
                                {isPendingSave ? "Saving..." : "Save"}
                            </Button>
                        </form>
                    </FormikProvider>)}
            </CardBody>
        </Card>
    );
}

export default FormCardOrderRule